import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiMonitor, FiTrendingUp, FiZap, FiShield, FiBarChart, FiGlobe, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './APM.css';

const ObservabilityPlatform = () => {
  useSEO({
    title: 'Observability Platform - AI-Powered Monitoring & Analytics | LogCrest',
    description: 'Complete observability platform for cloud-native applications. Monitor logs, metrics, traces, and infrastructure with AI-powered insights. Best observability platform for DevOps teams.',
    keywords: 'observability platform, system observability platform, cloud native observability, observability, full stack observability, application observability, infrastructure observability, best observability platform for startups'
  });

  useEffect(() => {
    trackPageView('/observability-platform', {
      page_title: 'LogCrest Observability Platform',
      page_category: 'observability-platform'
    });
  }, []);

  const features = [
    {
      icon: <FiMonitor />,
      title: 'Full-Stack Observability',
      description: 'Complete visibility across logs, metrics, traces, and infrastructure with unified dashboards.'
    },
    {
      icon: <FiZap />,
      title: 'AI-Powered Insights',
      description: 'Intelligent analysis and automated root cause detection using advanced machine learning.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Real-Time Monitoring',
      description: 'Monitor your entire stack in real-time with sub-second latency and instant alerting.'
    },
    {
      icon: <FiShield />,
      title: 'Cloud-Native Architecture',
      description: 'Built for Kubernetes, microservices, and modern cloud infrastructure.'
    },
    {
      icon: <FiBarChart />,
      title: 'Advanced Analytics',
      description: 'Deep analytics and correlation across all telemetry data for comprehensive insights.'
    },
    {
      icon: <FiGlobe />,
      title: 'Multi-Cloud Support',
      description: 'Monitor across AWS, GCP, Azure, and on-premise environments seamlessly.'
    }
  ];

  const benefits = [
    'Complete observability across logs, metrics, and traces',
    'AI-powered anomaly detection and root cause analysis',
    'Real-time monitoring with sub-second latency',
    'Cloud-native architecture for modern applications',
    'Unified dashboards for comprehensive visibility',
    'Best observability platform for startups and enterprises'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Complete Observability Platform</h1>
            <p className="hero-subtitle">
              Best observability platform for cloud-native applications. Monitor logs, metrics, traces, and infrastructure with AI-powered insights.
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                  <FiArrowRight />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>System Observability Platform</h2>
            <p>Cloud native observability for modern applications and infrastructure</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Observability Platform?</h2>
              <p>
                Our system observability platform provides complete visibility into your applications 
                and infrastructure with AI-powered insights and real-time monitoring.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Observability Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiMonitor />
                    <span>System Health: 99.9%</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Uptime: 100%</span>
                  </div>
                  <div className="metric">
                    <FiBarChart />
                    <span>Metrics: 2.5M/sec</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for Complete Observability?</h2>
            <p>Experience the best observability platform for your cloud-native applications.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ObservabilityPlatform;

